import React, {useMemo} from 'react';
import {Grid, Typography} from '@mui/material';
import {
  BuilderColumnWrapper,
  BuilderSourceColumnHeader,
  BuilderDestinationColumnHeader,
} from '../CreateBuilder/BuilderSourceDestination/BuilderSourceDestination';
import {BuilderPlanFormsStyled} from '../CreateBuilder/BuilderSourceDestination/BuilderPlanForm';
import {BuilderProps} from '../CreateBuilder/BuilderSourceDestination/builderValidators';
import {BuilderParserStep} from '../BuilderParserStep/BuilderParserStep';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {withdrawalFieldsType} from './builderWithdrawalFields';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {useResetBuilder} from '../CreateBuilder/BuilderSourceDestination/useResetBuilder';
import {
  changeWithdrawalDestinationFactory,
  changeWithdrawalSourceFactory,
  withdrawalSources,
} from './BuilderWithdrawalHelpers';
import {WithdrawalSourceParent} from './WithdrawalSourceParent';
import {WithdrawalDestinationBankAccount} from './WithdrawalDestinationBankAccount';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {
  useAutoForm,
  useAutoField,
  useLoadedAutoField,
} from '../../../components/AutoForm/useAutoForm';
import {useWithdrawalPotsQuery} from './useWithdrawalPotsQuery';
import {useSyncWithdrawalDestination} from '../hooks/builderWithdrawalHooks';

const NO_DESTINATION_TEXT =
  'No bank account to withdraw to. Check the client has a validated bank account, or for a third-party plan, that the plan or its provider product has one in Curo.';

export const Withdrawal = ({sourceDestinationLabels}: BuilderProps) => {
  const {sourcePots, destinationAccounts, isSourceChosen, areDestinationsLoaded} =
    useWithdrawalPotsQuery();
  const {loaded: _loaded, setLoaded: _setLoaded} = useLoadedAutoField();
  useResetBuilder(OVRecommendActionEnum.WITHDRAW);
  const {builderReviewId, isSPPDA} = useBuilderContext();
  const [withdrawalFieldObj, _withdrawMeta, _withdrawHelpers] =
    useAutoField<builderFieldsType['withdraw']>('withdraw');
  const {values} = useAutoForm<builderFieldsType>();
  const [withdrawSourceObj, withdrawSourceMeta, withdrawSourceHelpers] = useAutoField<
    withdrawalFieldsType['source'] | undefined
  >('withdraw.source');
  const [withdrawDestinationObj, withdrawDestinationMeta, withdrawDestinationHelpers] =
    useAutoField<withdrawalFieldsType['destination'] | undefined>('withdraw.destination');
  const {value: withdraw} = withdrawalFieldObj;

  const sources = useMemo(() => {
    return withdrawalSources({
      sourcePots,
      isSppDa: isSPPDA,
      reviewId: builderReviewId,
    });
  }, [sourcePots, isSPPDA, builderReviewId]);
  const changeSource = changeWithdrawalSourceFactory({
    sourcePots,
    withdraw,
    withdrawSourceHelpers,
  });
  const changeDestination = changeWithdrawalDestinationFactory({
    withdrawDestinationHelpers,
  });
  useSyncWithdrawalDestination({
    destinationAccounts,
    areDestinationsLoaded,
    selectedId: withdrawDestinationObj.value?.id,
    withdrawDestinationHelpers,
  });

  if (!values.mainPlanType) {
    return null;
  }
  return sourcePots?.length === 0 ? (
    <BuilderParserStep errorText="No accounts to withdraw from" />
  ) : (
    <BuilderColumnWrapper>
      <Grid item xs={6} xl={5.8} data-test="Source Choices">
        <BuilderSourceColumnHeader
          fromError={withdrawSourceMeta.error}
          sourceLabel={sourceDestinationLabels?.first}
        />
        <BuilderPlanFormsStyled>
          {sources.map((pot) => (
            <WithdrawalSourceParent
              key={pot.id}
              pot={pot}
              withdrawSourceId={withdrawSourceObj.value?.id}
              sameReview={pot.review?.id === builderReviewId}
              handleChange={changeSource}
            />
          ))}
        </BuilderPlanFormsStyled>
      </Grid>
      <Grid item xs={6} xl={5.8} data-test="Destination Choices">
        <BuilderDestinationColumnHeader
          toError={withdrawDestinationMeta.error}
          destinationLabel={sourceDestinationLabels?.second}
        />
        <BuilderPlanFormsStyled>
          {!isSourceChosen && <Typography>Select a plan to withdraw from</Typography>}
          {isSourceChosen &&
            areDestinationsLoaded &&
            destinationAccounts.length === 0 && (
              <BuilderParserStep errorText={NO_DESTINATION_TEXT} />
            )}
          {destinationAccounts.map((bankAccount) => (
            <WithdrawalDestinationBankAccount
              key={bankAccount.id}
              bankAccount={bankAccount}
              isSelected={withdrawDestinationObj.value?.id === bankAccount.id}
              handleChange={changeDestination}
            />
          ))}
        </BuilderPlanFormsStyled>
      </Grid>
    </BuilderColumnWrapper>
  );
};
